import React, { useEffect, useRef, useState } from 'react';
import { BarChart3, Coins, LogOut, ChevronDown, ShieldAlert, Film, ShieldCheck, LayoutGrid, FileText, ClipboardList } from 'lucide-react';
import { Avatar } from './Avatar';
import { ThemeToggle } from './ThemeToggle';
import type { Profile } from '../lib/api';

interface Props {
  profile: Profile;
  onProfile: () => void;
  onMyQuizzes: () => void;
  onMyExams: () => void;
  onMyTasks: () => void;
  onWallet: () => void;
  onGuardian: () => void;
  onRecordings: () => void;
  onAdmin: () => void;
  onSignOut: () => void;
}

/**
 * Menú de cuenta, colgado del avatar de la barra superior.
 *
 * El avatar es el disparador —no un icono aparte— porque es lo que la gente
 * busca para "sus cosas" en cualquier sitio web. Antes cerrar sesión era un
 * botón suelto al lado del nombre: fácil de pulsar sin querer y sin sitio
 * donde colgar el resto de opciones de la cuenta.
 *
 * Aquí dentro va todo lo de la cuenta: quién eres, el tema, el perfil, los
 * Dahus y la salida. La barra superior se queda solo con lo que se usa a
 * diario —el ranking y crear una trivia— en vez de siete iconos sueltos.
 */
export const UserMenu: React.FC<Props> = ({ profile, onProfile, onMyQuizzes, onMyExams, onMyTasks, onWallet, onGuardian, onRecordings, onAdmin, onSignOut }) => {
  const [open, setOpen] = useState(false);
  const contenedor = useRef<HTMLDivElement>(null);
  const primerItem = useRef<HTMLButtonElement>(null);

  // Cerrar al pulsar fuera y con Escape. `mousedown` y no `click`: si se espera
  // al click, pulsar otro botón de la página lo activa con el menú aún encima.
  useEffect(() => {
    if (!open) return;
    const fuera = (e: MouseEvent) => {
      if (contenedor.current && !contenedor.current.contains(e.target as Node)) setOpen(false);
    };
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', fuera);
    document.addEventListener('keydown', tecla);
    return () => {
      document.removeEventListener('mousedown', fuera);
      document.removeEventListener('keydown', tecla);
    };
  }, [open]);

  useEffect(() => {
    if (open) primerItem.current?.focus();
  }, [open]);

  const elegir = (accion: () => void) => () => {
    setOpen(false);
    accion();
  };

  return (
    <div className="relative" ref={contenedor}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full border border-transparent
                   hover:border-ink-700 hover:bg-ink-850 transition-colors"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Menú de tu cuenta"
      >
        <span className="flex w-8 h-8 rounded-full overflow-hidden ring-1 ring-ink-700 shrink-0">
          <Avatar config={profile.avatar as never} size={32} className="rounded-none! ring-0! shadow-none!" />
        </span>
        <span className="hidden md:inline text-sm text-ink-200 font-medium max-w-[9rem] truncate">
          {profile.displayName}
        </span>
        <ChevronDown
          className={`w-3.5 h-3.5 text-ink-500 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-72 surface-raised bg-ink-900 p-1.5 animate-pop origin-top-right"
        >
          <div className="flex items-center gap-3 px-2.5 py-2.5">
            <span className="flex w-10 h-10 rounded-full overflow-hidden ring-1 ring-ink-700 shrink-0">
              <Avatar config={profile.avatar as never} size={40} className="rounded-none! ring-0! shadow-none!" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-ink-100 truncate">{profile.username}</p>
              {/* Debajo, el nombre real: aquí solo lo ve su dueño, y sirve para
                  saber de un vistazo con qué cuenta se está jugando. */}
              {(profile.firstName || profile.lastName) && (
                <p className="text-[0.75rem] text-ink-500 truncate">
                  {[profile.firstName, profile.lastName].filter(Boolean).join(' ')}
                </p>
              )}
            </div>
            {/* El saldo es un dato de la cuenta, como el nombre: va aquí arriba
                y no mezclado con las secciones de la lista. */}
            <button
              role="menuitem"
              onClick={elegir(onWallet)}
              aria-label={`Dahus: ${profile.dahus.toLocaleString('es')}`}
              title="Tus Dahus"
              className="flex items-center gap-1.5 px-2.5 h-8 rounded-full shrink-0 border border-ember-500/30
                         bg-ember-500/10 text-ember-300 hover:bg-ember-500/20 transition-colors"
            >
              <Coins className="w-3.5 h-3.5" />
              <span className="text-sm font-bold font-mono tabular-nums">{profile.dahus.toLocaleString('es')}</span>
            </button>
          </div>

          <div className="h-px bg-ink-800 my-1" />

          {/* El tema vive aquí y no suelto en la barra: es una preferencia de
              la cuenta, no una acción que se use en cada visita. */}
          <div className="px-2.5 py-2">
            <p className="text-[0.6875rem] uppercase tracking-wider text-ink-500 mb-1.5">Tema</p>
            <ThemeToggle />
          </div>

          <div className="h-px bg-ink-800 my-1" />

          <ItemMenu
            ref={primerItem}
            icono={<BarChart3 className="w-4 h-4" />}
            etiqueta="Perfil y estadísticas"
            descripcion="Cambia tu usuario y tu avatar"
            onClick={elegir(onProfile)}
          />
          <ItemMenu
            icono={<LayoutGrid className="w-4 h-4" />}
            etiqueta="Mis trivias"
            descripcion="Las que has creado"
            onClick={elegir(onMyQuizzes)}
          />
          <ItemMenu
            icono={<FileText className="w-4 h-4" />}
            etiqueta="Mis exámenes"
            descripcion="PIN, ajustes y notas"
            onClick={elegir(onMyExams)}
          />
          <ItemMenu
            icono={<ClipboardList className="w-4 h-4" />}
            etiqueta="Mis tareas"
            descripcion="Entregas en PDF y notas"
            onClick={elegir(onMyTasks)}
          />

          {profile.canMonitor && (
            <ItemMenu
              icono={<Film className="w-4 h-4" />}
              etiqueta="Grabaciones"
              descripcion="Pantallas de tus partidas supervisadas"
              onClick={elegir(onRecordings)}
            />
          )}

          {profile.isAdmin && (
            <>
              <div className="h-px bg-ink-800 my-1" />
              <ItemMenu
                icono={<ShieldCheck className="w-4 h-4" />}
                etiqueta="Administración"
                descripcion="Usuarios, actividad y salas"
                onClick={elegir(onAdmin)}
              />
            </>
          )}

          {profile.role === 'mega_guardian' && (
            <>
              <div className="h-px bg-ink-800 my-1" />
              <ItemMenu
                icono={<ShieldAlert className="w-4 h-4" />}
                etiqueta="Claves de IA"
                descripcion="Panel del mega guardián"
                onClick={elegir(onGuardian)}
              />
            </>
          )}

          <div className="h-px bg-ink-800 my-1" />

          <ItemMenu
            icono={<LogOut className="w-4 h-4" />}
            etiqueta="Cerrar sesión"
            peligro
            onClick={elegir(onSignOut)}
          />
        </div>
      )}
    </div>
  );
};

const ItemMenu = React.forwardRef<
  HTMLButtonElement,
  {
    icono: React.ReactNode;
    etiqueta: string;
    descripcion?: string;
    valor?: string;
    peligro?: boolean;
    onClick: () => void;
  }
>(({ icono, etiqueta, descripcion, valor, peligro, onClick }, ref) => (
  <button
    ref={ref}
    role="menuitem"
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-left transition-colors
                ${peligro ? 'text-danger-400 hover:bg-danger-400/10' : 'text-ink-200 hover:bg-ink-850'}`}
  >
    <span className={peligro ? '' : 'text-ink-400'}>{icono}</span>
    <span className="flex-1 min-w-0">
      <span className="block text-sm font-medium">{etiqueta}</span>
      {descripcion && (
        <span className="block text-[0.6875rem] text-ink-500 leading-snug">{descripcion}</span>
      )}
    </span>
    {valor && <span className="text-sm font-semibold text-ember-300 tabular-nums">{valor}</span>}
  </button>
));
ItemMenu.displayName = 'ItemMenu';
